import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

const MAX_RELOAD_ATTEMPTS = 3;
const CHUNK_ERROR_RELOAD_COUNT_ITEM = "chunkErrorReloadCount";

/**
 * ErrorBoundary que captura erros de lazy loading e força um reload da página.
 *
 * Isso resolve o erro "Failed to fetch dynamically imported module" que ocorre
 * quando um deploy é feito e o usuário tem uma página antiga aberta tentando
 * carregar chunks que foram deletados.
 *
 * OBS.: Error boundaries em React precisam ser classes para React <= 18
 */
export class LazyErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): State {
    return { hasError: true };
  }

  private handleChunkError = () => {
    const reloadCount = parseInt(sessionStorage.getItem(CHUNK_ERROR_RELOAD_COUNT_ITEM) || "0", 10);

    if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
      sessionStorage.removeItem(CHUNK_ERROR_RELOAD_COUNT_ITEM);
      console.error(`Max reload attempts (${MAX_RELOAD_ATTEMPTS}) reached. Showing error fallback.`);
      return false;
    }

    sessionStorage.setItem(CHUNK_ERROR_RELOAD_COUNT_ITEM, String(reloadCount + 1));
    console.warn(`Chunk load error detected, reloading page... (attempt ${reloadCount + 1}/${MAX_RELOAD_ATTEMPTS})`);

    // Cache busting: adiciona timestamp para evitar uso de cache antigo
    const timestamp = Date.now();
    const url = new URL(window.location.href);
    url.searchParams.set("_t", String(timestamp));
    window.location.href = url.toString();

    return true;
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const isChunkError =
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Loading chunk") ||
      error.message.includes("Loading CSS chunk");

    if (isChunkError) {
      this.handleChunkError();
      return;
    }

    // Re-lança outros erros para serem tratados por outros boundaries
    console.error("Unhandled error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Verifica se o limite de reloads foi atingido
      const reloadCount = parseInt(sessionStorage.getItem(CHUNK_ERROR_RELOAD_COUNT_ITEM) || "0", 10);

      // Se já tentamos o máximo de vezes, mostra mensagem de erro
      if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
        return (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <h3>Erro ao carregar a página</h3>

            <p>
              Não foi possível carregar os recursos necessários.
              <br />
              Tente limpar o cache do navegador ou entre em contato com o suporte.
            </p>
          </div>
        );
      }

      // Durante o reload, retorna null
      return null;
    }

    return this.props.children;
  }
}
